import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Deployment {
  id: string
  model: string
  version: string
  endpoint: string
  replicas: number
  cpu: string
  memory: string
  status: string
}

interface Endpoint {
  endpoint: string
  requests_hr: number
  latency_p50: number
  latency_p99: number
  errors: number
}

interface Statistics {
  deployed_models: number
  endpoints_active: number
  requests_today: number
  avg_latency_ms: number
  success_rate: number
  rollbacks_mtd: number
}

export default function ModelDeploymentPage() {
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [deploymentsRes, endpointsRes, statsRes] = await Promise.all([
        fetch('/api/modeldeployment/deployments'),
        fetch('/api/modeldeployment/endpoints'),
        fetch('/api/modeldeployment/statistics')
      ])
      if (deploymentsRes.ok) setDeployments(await deploymentsRes.json())
      if (endpointsRes.ok) setEndpoints(await endpointsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching model deployment:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Healthy') return 'bg-green-100 text-green-800'
    if (status === 'Degraded') return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Model Deployment</h1>
          <p className="mt-2 text-gray-600">Phase 273 - Serving infrastructure and endpoints</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Deployed Models</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.deployed_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Endpoints</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.endpoints_active}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Requests Today</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.requests_today)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Latency</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_latency_ms}ms</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.success_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Rollbacks MTD</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.rollbacks_mtd}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Endpoint Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requests/hr</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P50 Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P99 Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {endpoints.map((ep, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-mono text-blue-600">{ep.endpoint}</td>
                    <td className="px-6 py-4 font-medium">{formatNumber(ep.requests_hr)}</td>
                    <td className="px-6 py-4 text-green-600">{ep.latency_p50}ms</td>
                    <td className="px-6 py-4 text-orange-600">{ep.latency_p99}ms</td>
                    <td className="px-6 py-4 text-red-600">{ep.errors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Active Deployments</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Replicas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resources</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deployments.map((dep) => (
                  <tr key={dep.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{dep.model}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{dep.version}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{dep.replicas}</td>
                    <td className="px-6 py-4 text-gray-500">{dep.cpu} / {dep.memory}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(dep.status)}`}>{dep.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
